import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import posthogLib from 'posthog-js';

import httpInstances from '@/api/axiosInstance';
import { METADATA_TERMS_FIELD } from '@/constants/constants';
import { setTripTokenToStorage } from '@/services/storage';
import { UserAccess, UserMetadata, UserSubscription } from '@/types/auth';
import { detectEnvironment } from '@/utils/detectEnvironment';
import { retrieveFromUserMetadata } from '@/utils/retrieveFromUserMetadata';

export type TripTokenState = {
  status: 'idle' | 'loading' | 'failed' | 'fulfilled' | 'expired';
  error: string | null;
  userEmail: string | null;
  userAccess: UserAccess | null;
  metadata: UserMetadata[] | null;
  terms: string | null;
  hsObjectId: string | null;
  subscription: UserSubscription | null;
};

const initialState: TripTokenState = {
  status: 'idle',
  error: null,
  userEmail: null,
  userAccess: null,
  hsObjectId: null,
  terms: null,
  metadata: null,
  subscription: null
};

export const loginTrip = createAsyncThunk(
  'loginTrip',

  async (gxToken: string, { rejectWithValue }) => {
    try {
      const { data } = await httpInstances.axiosInternalInstance.get(
        `${detectEnvironment() === 'staging' ? '/staging/' : '/'}token`,
        {
          params: { token: gxToken }
        }
      );
      setTripTokenToStorage(data.token);
      return data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue({ data: error.response.data, status: error.response.status });
      }
      posthogLib.capture('user_token_error', {
        error
      });
      throw error;
    }
  }
);

const tripTokenSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    acceptTerms(state, action: PayloadAction<string>) {
      const terms = action.payload;
      state.terms = terms;
      saveTerms(terms, state.userEmail ?? '');
    },
    resetStatus(state) {
      state.status = 'idle';
    }
  },
  extraReducers: builder => {
    builder
      .addCase(loginTrip.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginTrip.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.userEmail = action.payload?.userEmail;
        state.userAccess = UserAccess[action.payload?.access as UserAccess];
        state.hsObjectId = action.payload?.hsObjectId;
        state.metadata = action.payload?.metadata;
        state.terms = retrieveFromUserMetadata(action.payload?.metadata, METADATA_TERMS_FIELD);
        state.subscription = action.payload?.subscription;
      })
      .addCase(loginTrip.rejected, (state, action) => {
        state.status = 'failed';
        if (action.payload) {
          const response = action.payload as { data: string; status: number };
          state.error = response.data ?? 'Login failed';
        } else {
          state.error = action.error.message ?? 'Login failed';
        }
      });
  }
});
const saveTerms = async (terms: string, email: string) => {
  return httpInstances.axiosGoInstance.patch(`/users/${email}`, { terms });
};

export const { resetStatus, acceptTerms } = tripTokenSlice.actions;

export default tripTokenSlice.reducer;
